import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ReferralRelativesChoice } from '@/components/patient/ReferralRelativesChoice';
import { selectedReferralRelative, type RelativeChoice } from '@/lib/referral/referralRelative';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import { Hospital, Send } from 'lucide-react';
import { patientHenvisningPath, patientTabPath } from '@/lib/patientRecordPaths';
import {
  ReferralBuiltPreview,
  type ReferralCreatedInfo,
} from '@/components/patient/ReferralBuiltPreview';
import { Grid, RadioGroup, ReadOnly, ReferralBlockingNotice, ReferralFormChrome, Section, TextArea, ReferralSubmitError } from '@/components/patient/ReferralFormLayout';
import { SorPicker, type PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/lib/auth';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useDecryptCpr } from '@/hooks/usePatients';
import { usePatientRelations } from '@/hooks/usePatientRelations';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { ApiCallError, callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import {
  MEDCOM_MAX_TELEPHONE_SUBSCRIBERS,
  readTelephoneSubscribers,
  type MedcomTelephoneCode,
} from '@/lib/patientStamkortFields';
import { formatCprDisplay } from '@/lib/cpr';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { medicalSpecialityLabel } from '@/lib/referral/medicalSpecialityLabel';
import {
  REFERENCE_DESCRIPTION_MAX,
  CLINICAL_INFO_MAX_TOTAL_XREF06,
  PARTY_MAX,
  WARNING_ANAMNESIS,
  WARNING_DIAGNOSIS,
  cprDigits,
  firstNonLatin1,
  isLatin1,
  medcomRelationCode,
  newEpisodeIdentifier,
  readPostalAddress,
  receiverIdentifierPair,
  senderContactGaps,
  stamkortLengthViolations,
  isUnder15,
  under15BlocksReferral,
  type IdentifierPairGap,
  type ReceiverIdentifierParty,
  type SenderContactGap,
  type StamkortField,
  type Xref01IdentifierCode,
} from '@/lib/referral/xref01Validation';
import {
  WARNING_UNDER15_CHOOSE_RELATIVE,
  WARNING_UNDER15_NO_RELATIVE,
  WARNING_UNDER15_RELATIONS_ERROR,
} from '@/lib/referral/xref01Codes';
import {
  XML_ATTACHMENT_ACCEPT,
  checkReferralFile,
  medbinObjectType,
  needsZipObjectType,
  referralReferencesForRequest,
  type BinReference,
  type ZipObjectType,
  type FileAttachmentDraft,
  type ReferralFileLimitCode,
  type ReferralReferenceIssue,
} from '@/lib/referral/referralAttachments';
import { ZipContentSelect } from '@/components/patient/ZipContentSelect';
import {
  XREF06_CLINICAL_REASON_LABEL,
  XREF06_CLINICAL_REASON_MAX,
  xref06AdditionalInformation,
  xref06ClinicalReasonTooLong,
} from '@/lib/referral/xref06ClinicalReason';
import { referralBuildFailureMessage } from '@/lib/referral/referralBuildError';
import {
  XREF06_LOOKUP_RECEIVES,
  dnhfBlocked,
  receivesEdifactOnly,
  ref06OnlyBlocked,
} from '@/lib/referral/xref06Delivery';
import { FORM_ERROR_TYPE, FORM_LABEL_TYPE, TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { Json } from '@/types/database.generated';

export interface SpecialistReferralPatient {
  id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  job_title: string | null;
  address: Json;
  telephone_subscribers: Json | null;
  has_cpr: boolean;
}

const DIAGNOSE_CODES = ['1', '3', '4', '5', '6'] as const;

/**
 * A speciallægehenvisning can go to a receiver that reads XREF06, or to one that
 * reads only REF06 (EDIFACT). The latter is converted. MedCom REF06-Send 3.5.
 */
const SPECIALIST_RECEIVES = XREF06_LOOKUP_RECEIVES;

type SpecialistDiagnoseCode = (typeof DIAGNOSE_CODES)[number];

const DIAGNOSE_TYPE_CODE = 'speciallaegehenvisning';

const ANAMNESIS_PROMPT =
  'Henvisningen skal indeholde oplysning om hvilken lidelse, der henvises for, endvidere beskrives relevante kliniske fund, herunder hvor på/i kroppen, og resultatet af eventuelle undersøgelse(r). Det oplyses også, om der har været forsøgt behandling, med hvad, og hvilken effekt det har haft.';

const FAPS_ENKELTSTAAENDE_URL =
  'https://www.laeger.dk/media/ialdawid/faps_endelig_oversigt_over_maalgrupper_enkeltstaaende_konsultation_-_efter_opsaetning_0.pdf';

const EBOKS_CONSENT_SENTENCE = {
  ja: 'Pt. har givet samtykke til indkaldelse via e-boks/mit.dk',
  nej: 'Pt. har ikke givet samtykke til indkaldelse via e-boks/mit.dk',
  ikke_oplyst: 'Samtykke ikke oplyst til indkaldelse via e-boks/mit.dk',
} as const;

type EboksConsent = keyof typeof EBOKS_CONSENT_SENTENCE;

const STAMKORT_LABEL: Record<StamkortField, string> = {
  surname: 'Efternavn',
  givenName: 'Fornavn',
  jobTitle: 'Stilling',
  street: 'Adresse',
  postCode: 'Postnr',
  district: 'By',
  email: 'E-mail',
  telephone: 'Telefonnummer',
};

const PHONE_ROWS: { code: MedcomTelephoneCode; label: string }[] = [
  { code: 'fastnet', label: 'Fastnet' },
  { code: 'mobil', label: 'Mobil' },
  { code: 'arbejde', label: 'Arbejde' },
];

type RefCheck = { id: string; pass: boolean; detail: string };
type RefPreview = {
  ok: boolean;
  xml: string;
  checks: RefCheck[];
  /** The same letter as EDIFACT. Present when the receiver reads REF06 only. */
  edi?: string;
  id?: string;
  outboxId?: string;
};

type FocusTarget = 'diagnosis' | 'anamnesis';

interface Xref06Request extends Record<string, unknown> {
  clinicId: string;
  patientId: string;
  kind: 'XREF06';
  acknowledgementCode: 'pluspositivkvitt';
  sender: {
    ean: string;
    identifier: string;
    identifierCode: string;
    organisationName: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
    telephoneSubscriberIdentifier?: string;
    medicalSpecialityCode: string;
    referrer: { personName: string; personTitle: string };
  };
  receiver: {
    ean: string;
    identifier: string;
    identifierCode: string;
    organisationName?: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
  };
  patient: {
    civilRegistrationNumber: string;
    personSurnameName: string;
    personGivenName?: string;
    personTitle?: string;
    streetName?: string;
    districtName?: string;
    postCodeIdentifier?: string;
    emailIdentifier?: string;
    telephoneSubscribers?: {
      telephoneSubscriberIdentifier: string;
      telephoneSubscriberCode: string;
    }[];
  };
  relative?: {
    relationCode: string;
    personIdentifier?: string;
    personSurnameName?: string;
    personGivenName?: string;
  };
  episodeOfCareIdentifier: string;
  relevantClinicalInformation: {
    anamnesis: string[];
  };
  additionalInformation?: { clinicalReason: string[] };
  referral: {
    refer: {
      diagnoseCode: SpecialistDiagnoseCode;
      diagnoseTypeCode: typeof DIAGNOSE_TYPE_CODE;
      diagnoseText: string;
    };
  };
  references?: BinReference[];
  companions?: { exchangeId: string; fileName: string }[];
}

function isSpecialistDiagnoseCode(value: string): value is SpecialistDiagnoseCode {
  return (DIAGNOSE_CODES as readonly string[]).includes(value);
}

function diagnoseLabel(code: SpecialistDiagnoseCode): string {
  switch (code) {
    case '1':
      return 'Udredning og behandling';
    case '3':
      return 'Radiologi';
    case '4':
      return 'Fortsat behandling';
    case '5':
      return 'Besøg';
    case '6':
      return 'Vurdering ved enkeltstående konsultation';
    default: {
      const unexpected: never = code;
      return unexpected;
    }
  }
}

export function SpecialistReferralForm({
  patient,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  patient: SpecialistReferralPatient;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel?: () => void;
  onCreated?: (created: ReferralCreatedInfo) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clinicQuery = useClinic(patient.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(patient.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const relationsQuery = usePatientRelations(patient.id);
  const decryptPatient = useDecryptCpr();
  const decryptRelative = useDecryptCpr();
  const medicalSpecialityCode = readClinicMedicalSpecialityCode(
    connectionQuery.data?.medical_speciality_code,
  );

  const [episodeId] = useState(newEpisodeIdentifier);
  const [anamnesis, setAnamnesis] = useState('');
  const [clinicalReason, setClinicalReason] = useState('');
  const [eboksConsent, setEboksConsent] = useState<EboksConsent>('ikke_oplyst');
  const [diagnoseCode, setDiagnoseCode] = useState<SpecialistDiagnoseCode | null>(null);
  const [receiver, setReceiver] = useState<PickedReceiver | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [cprFailed, setCprFailed] = useState(false);
  const [relativeCpr, setRelativeCpr] = useState<string | null>(null);
  const [relativeChoice, setRelativeChoice] = useState<RelativeChoice>(null);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RefPreview | null>(null);
  const [attachments, setAttachments] = useState<SpecialistAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const diagnosisRef = useRef<HTMLSelectElement>(null);
  const anamnesisRef = useRef<HTMLTextAreaElement>(null);
  const clinicalReasonRef = useRef<HTMLTextAreaElement>(null);
  const onCprRevealedRef = useRef(onCprRevealed);
  onCprRevealedRef.current = onCprRevealed;

  const referrerQuery = useReferrerIdentity(user?.id, patient.clinic_id);

  useEffect(() => {
    if (!patient.has_cpr || revealedCpr || !canRevealCpr) return;
    let cancelled = false;
    void decryptPatient
      .mutateAsync({
        patient_id: patient.id,
        source: 'specialist-referral',
        purpose: 'xref06',
      })
      .then(
        (result) => {
          if (!cancelled) onCprRevealedRef.current(result.cpr);
        },
        () => {
          if (!cancelled) setCprFailed(true);
        },
      );
    return () => {
      cancelled = true;
    };
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, patient.has_cpr, patient.id, revealedCpr]);

  const address = readPostalAddress(patient.address);
  const subscribers = readTelephoneSubscribers(patient.telephone_subscribers);
  const clinicAddress = readPostalAddress(clinicQuery.data?.address);
  const personRelations = (relationsQuery.data ?? []).flatMap((row) => {
    if (!row.to_patient_id || medcomRelationCode(row.relation_type) === null) return [];
    return [{ relationType: row.relation_type, row }];
  });
  const relativeCprDigits = revealedCpr ? cprDigits(revealedCpr) : null;
  const relativeUnder15 = Boolean(relativeCprDigits && isUnder15(relativeCprDigits, new Date()));
  const chosenRelative = selectedReferralRelative(personRelations, relativeChoice, relativeUnder15);
  const chosenPatientId = chosenRelative?.row.to_patient_id ?? null;
  const relatedIds = [...new Set(personRelations.map((item) => item.row.to_patient_id as string))].sort();

  const relatedQuery = useQuery({
    queryKey: ['specialist-referral-relatives', patient.id, relatedIds],
    enabled: relatedIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name')
        .in('id', relatedIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!chosenPatientId || !canRevealCpr) {
      setRelativeCpr(null);
      return;
    }
    let cancelled = false;
    setRelativeCpr(null);
    void decryptRelative
      .mutateAsync({
        patient_id: chosenPatientId,
        source: 'specialist-referral-relative',
        purpose: 'xref06',
      })
      .then(
        (result) => {
          if (!cancelled) setRelativeCpr(result.cpr);
        },
        () => {
          if (!cancelled) setRelativeCpr(null);
        },
      );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, chosenPatientId]);

  const emptyLabel = t('patients.hospital_referral.empty', 'Ikke udfyldt');
  const patientCprDigits = revealedCpr ? cprDigits(revealedCpr) : null;
  const today = new Date();
  const childWithoutRelative = Boolean(
    patientCprDigits &&
      relationsQuery.isSuccess &&
      under15BlocksReferral(patientCprDigits, personRelations.map((item) => item.relationType), today),
  );
  const eboksSentence = EBOKS_CONSENT_SENTENCE[eboksConsent];
  const anamnesisSentLength = anamnesis.length + eboksSentence.length;
  const anamnesisOver = anamnesisSentLength > CLINICAL_INFO_MAX_TOTAL_XREF06;
  const anamnesisLabel = t(
    'patients.specialist_referral.anamnesis',
    'Lægens oplysninger til speciallægen',
  );
  const clinicalReasonLabel = t('patients.specialist_referral.clinical_reason', XREF06_CLINICAL_REASON_LABEL);
  const clinicalReasonOver = xref06ClinicalReasonTooLong(clinicalReason);

  function tooLong(field: string, length: number, max: number): string {
    return t(
      'patients.hospital_referral.too_long',
      '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
      { field, length, max },
    );
  }

  function latin1Message(field: string): string {
    return t(
      'patients.hospital_referral.latin1',
      '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
      { field },
    );
  }

  function anamnesisTooLong(): string {
    return tooLong(anamnesisLabel, anamnesisSentLength, CLINICAL_INFO_MAX_TOTAL_XREF06);
  }

  function attachmentLimitMessage(code: ReferralFileLimitCode): string {
    switch (code) {
      case 'unsupported-type':
        return t(
          'patients.specialist_referral.attachments_type',
          'Denne filtype kan ikke sendes. Tilladte typer: pdf, jpg, jpeg, png, tif, tiff, bmp, wav, scp, gif, txt, rtf, doc, xls og dcm.',
        );
      case 'file-too-large':
        return t('patients.specialist_referral.attachments_too_large', 'En fil må højst være 100 MB.');
      case 'too-many-files':
        return t('patients.specialist_referral.attachments_too_many', 'Der kan højst vedlægges 10 filer.');
      case 'total-too-large':
        return t('patients.specialist_referral.attachments_total', 'Filerne er tilsammen over 100 MB.');
      default: {
        const unexpected: never = code;
        return unexpected;
      }
    }
  }

  function attachmentIssueMessage(issue: ReferralReferenceIssue): string {
    switch (issue.source) {
      case 'budget':
        return attachmentLimitMessage('too-many-files');
      case 'url':
        return attachmentUrlIssueMessage(issue);
      case 'file':
        return attachmentFileIssueMessage(issue);
      default: {
        const unexpected: never = issue;
        return unexpected;
      }
    }
  }

  function attachmentUrlIssueMessage(issue: ReferralReferenceIssue & { source: 'url' }): string {
    const nameLabel = t('patients.specialist_referral.attachments_name', 'Filnavn');
    switch (issue.code) {
      case 'incomplete':
      case 'too_many':
        return attachmentLimitMessage('too-many-files');
      case 'latin1':
        return latin1Message(nameLabel);
      case 'description_long':
      case 'url_long':
        return tooLong(nameLabel, issue.length, issue.max);
      default: {
        const unexpected: never = issue;
        return unexpected;
      }
    }
  }

  function attachmentFileIssueMessage(issue: Extract<ReferralReferenceIssue, { source: 'file' }>): string {
    const nameLabel = t('patients.specialist_referral.attachments_name', 'Filnavn');
    switch (issue.code) {
      case 'incomplete':
        return t('patients.specialist_referral.attachments_unreadable', 'Filen kunne ikke læses.');
      case 'latin1':
        return latin1Message(nameLabel);
      case 'description_long':
        return tooLong(nameLabel, issue.length, issue.max);
      case 'unsupported-type':
        return attachmentLimitMessage('unsupported-type');
      case 'file-too-large':
        return attachmentLimitMessage('file-too-large');
      case 'too-many-files':
        return attachmentLimitMessage('too-many-files');
      case 'total-too-large':
        return attachmentLimitMessage('total-too-large');
      case 'not_ready':
        return t(
          'patients.specialist_referral.attachments_not_ready',
          'Bilag {{number}}: filen er ikke lagt på bilaget.',
          { number: issue.index + 1 },
        );
      case 'zip_type_missing':
        return t(
          'patients.hospital_referral.attachment_zip_type_missing',
          'Bilag {{number}}: vælg, hvad ZIP-filen indeholder.',
          { number: issue.index + 1 },
        );
      default: {
        const unexpected: never = issue;
        return unexpected;
      }
    }
  }

  function takenAttachment(
    file: File,
    existing: readonly SpecialistAttachment[],
  ): SpecialistAttachment | string {
    const limit = checkReferralFile({
      fileName: file.name,
      byteLength: file.size,
      existingFileCount: existing.length,
      existingTotalBytes: existing.reduce((sum, item) => sum + item.byteLength, 0),
    });
    if (!limit.ok) return attachmentLimitMessage(limit.code);
    const description = file.name.trim();
    const nameLabel = t('patients.specialist_referral.attachments_name', 'Filnavn');
    if (description.length === 0) {
      return t('patients.hospital_referral.attachment_file_unreadable', 'Filen kunne ikke læses.');
    }
    if (description.length > REFERENCE_DESCRIPTION_MAX) {
      return tooLong(nameLabel, description.length, REFERENCE_DESCRIPTION_MAX);
    }
    if (!isLatin1(description)) return latin1Message(nameLabel);
    return {
      key: newEpisodeIdentifier(),
      description,
      fileName: description,
      byteLength: file.size,
      file,
      objectIdentifier: '',
      objectCode: '',
      objectExtensionCode: '',
      originalObjectSize: null,
      exchangeId: null,
      error: '',
    };
  }

  function onPickAttachments(picked: readonly File[]) {
    if (picked.length === 0) return;
    setAttachmentError(null);
    let next = attachments;
    let refused: string | null = null;
    for (const file of picked) {
      const result = takenAttachment(file, next);
      if (typeof result === 'string') {
        refused = result;
        break;
      }
      next = [...next, result];
    }
    setAttachments(next);
    setAttachmentError(refused);
  }

  function presentDetail(detail: string): string {
    const diagnosis = t('patients.hospital_referral.diagnosis_required', WARNING_DIAGNOSIS);
    return detail
      .replaceAll('Refer/DiagnoseCode', diagnosis)
      .replaceAll('Anamnesis exceeds tx..6300', anamnesisTooLong());
  }

  function assess(): {
    blocking: string[];
    required: string[];
    pending: string[];
    warnings: string[];
    focus: FocusTarget | null;
  } {
    const blocking: string[] = [];
    const required: string[] = [];
    const pendingMessages: string[] = [];
    const warnings: string[] = [];

    if (medicalSpecialityCode === null) {
      blocking.push(
        t(
          'patients.hospital_referral.speciality_block',
          'Klinikkens specialekode er ikke sat op. Henvisningen kan ikke dannes, før specialekoden er konfigureret.',
        ),
      );
    }

    for (const violation of stamkortLengthViolations({
      surname: patient.last_name,
      givenName: patient.first_name,
      jobTitle: patient.job_title ?? '',
      street: address.street,
      postCode: address.postCode,
      district: address.district,
      email: patient.email ?? '',
      telephones: subscribers.map((subscriber) => subscriber.identifier),
    })) {
      blocking.push(tooLong(t(`patients.hospital_referral.stamkort_${violation.field}`, STAMKORT_LABEL[violation.field]), violation.length, violation.max));
    }
    if (subscribers.length > MEDCOM_MAX_TELEPHONE_SUBSCRIBERS) {
      blocking.push(
        t(
          'patients.hospital_referral.too_many_phones',
          'Stamkortet har mere end 3 telefonnumre. MedCom tillader højst 3.',
        ),
      );
    }

    if (!patient.has_cpr) {
      blocking.push(
        t('patients.hospital_referral.cpr_missing', 'Patienten har ikke et CPR-nummer. Henvisningen kan ikke dannes.'),
      );
    } else if (!canRevealCpr) {
      blocking.push(
        t('patients.hospital_referral.cpr_unavailable', 'CPR-nummeret kan ikke vises. Henvisningen kan ikke dannes.'),
      );
    } else if (cprFailed) {
      blocking.push(
        t('patients.hospital_referral.cpr_failed', 'CPR-nummeret kunne ikke læses. Henvisningen kan ikke dannes.'),
      );
    } else if (!revealedCpr) {
      pendingMessages.push(t('patients.hospital_referral.cpr_loading', 'CPR-nummeret indlæses stadig.'));
    } else if (!patientCprDigits) {
      blocking.push(
        t('patients.hospital_referral.cpr_format', 'CPR-nummeret har ikke 10 cifre. Henvisningen kan ikke dannes.'),
      );
    }

    if (patientCprDigits && relationsQuery.isLoading) {
      pendingMessages.push(
        t('patients.hospital_referral.relatives_loading', 'Relationerne indlæses stadig.'),
      );
    }
    if (patientCprDigits && relationsQuery.isError && isChildAge(patientCprDigits, today)) {
      blocking.push(
        t(
          'patients.hospital_referral.relatives_error_under15',
          WARNING_UNDER15_RELATIONS_ERROR,
        ),
      );
    }
    if (childWithoutRelative) {
      blocking.push(
        t(
          'patients.hospital_referral.relatives_under15',
          WARNING_UNDER15_NO_RELATIVE,
        ),
      );
    }
    if (chosenRelative && relatedQuery.isLoading) {
      pendingMessages.push(
        t('patients.hospital_referral.relative_loading', 'Pårørendes navn indlæses stadig.'),
      );
    }
    if (chosenRelative && relatedQuery.isError) {
      blocking.push(
        t(
          'patients.hospital_referral.relative_failed',
          'Pårørendes navn kunne ikke hentes fra stamkortet. Henvisningen kan ikke dannes.',
        ),
      );
    }
    if (chosenRelative && relatedQuery.isSuccess) {
      const names = relatedQuery.data?.find((row) => row.id === chosenRelative.row.to_patient_id);
      if (!names?.last_name) {
        blocking.push(
          t(
            'patients.hospital_referral.relative_failed',
            'Pårørendes navn kunne ikke hentes fra stamkortet. Henvisningen kan ikke dannes.',
          ),
        );
      }
    }

    if (!receiver) {
      required.push(t('patients.hospital_referral.receiver_missing', 'Vælg en modtager.'));
    } else {
      const blocked = receiverBlockedReason(receiver.organisation);
      if (blocked) blocking.push(blocked);
      blocking.push(
        ...partyProblems(
          t,
          tooLong,
          t('patients.hospital_referral.receiver_section', 'Modtager'),
          receiver,
        ),
      );
    }

    if (!diagnoseCode) {
      required.push(t('patients.hospital_referral.diagnosis_required', WARNING_DIAGNOSIS));
    }

    if (anamnesis.trim().length === 0) {
      required.push(t('patients.hospital_referral.anamnesis_required', WARNING_ANAMNESIS));
    }
    if (anamnesisOver) blocking.push(anamnesisTooLong());
    if (clinicalReasonOver) {
      blocking.push(tooLong(clinicalReasonLabel, clinicalReason.trim().length, XREF06_CLINICAL_REASON_MAX));
    }
    const attachmentResult = referralReferencesForRequest(attachments.map(attachmentDraft), {
      requireChecked: false,
    });
    if (!attachmentResult.ok) {
      for (const issue of attachmentResult.issues) {
        blocking.push(attachmentIssueMessage(issue));
      }
    }

    const textFields: { field: string; value: string }[] = [
      { field: anamnesisLabel, value: anamnesis },
      { field: clinicalReasonLabel, value: clinicalReason },
      { field: STAMKORT_LABEL.surname, value: patient.last_name },
      { field: STAMKORT_LABEL.givenName, value: patient.first_name },
      { field: STAMKORT_LABEL.jobTitle, value: patient.job_title ?? '' },
      { field: STAMKORT_LABEL.street, value: address.street },
      { field: STAMKORT_LABEL.postCode, value: address.postCode },
      { field: STAMKORT_LABEL.district, value: address.district },
      { field: STAMKORT_LABEL.email, value: patient.email ?? '' },
      ...subscribers.map((subscriber) => ({ field: subscriber.code, value: subscriber.identifier })),
    ];
    if (receiver) {
      textFields.push({ field: t('patients.hospital_referral.identifier', 'Identifikator'), value: receiver.identifier });
    }
    if (diagnoseCode) {
      textFields.push({
        field: t('patients.hospital_referral.diagnosis_section', 'Henvisningsdiagnose'),
        value: diagnoseLabel(diagnoseCode),
      });
    }
    for (const field of textFields) {
      if (!isLatin1(field.value)) blocking.push(latin1Message(field.field));
    }

    blocking.push(...senderProblems(pendingMessages));
    warnings.push(...senderContactWarningMessages());

    return {
      blocking,
      required,
      pending: pendingMessages,
      warnings,
      focus: !diagnoseCode ? 'diagnosis' : anamnesis.trim().length === 0 ? 'anamnesis' : null,
    };
  }

  function senderProblems(pendingMessages: string[]): string[] {
    const problems: string[] = [];
    if (clinicQuery.isLoading || connectionQuery.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.sender_loading', 'Afsender indlæses stadig.'));
      return problems;
    }
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    if (!clinic || !connection || connection.status !== 'active') {
      problems.push(t('patients.hospital_referral.sender_missing', 'Afsenderen er ikke tilgængelig.'));
      return problems;
    }
    const ean = connection.ean ?? '';
    if (ean.length === 0) {
      problems.push(t('patients.hospital_referral.sender_ean_missing', 'Afsender mangler EAN.'));
    } else if (ean.length > PARTY_MAX.ean) {
      problems.push(tooLong('EAN', ean.length, PARTY_MAX.ean));
    }
    if (!connection.sor_id) {
      problems.push(t('patients.hospital_referral.sender_sor_missing', 'Klinikken har ikke et SOR-id.'));
    } else if (senderSor.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.sender_sor_loading', 'Lokationsnummer indlæses stadig.'));
    } else if (senderSor.error || !senderSor.data) {
      problems.push(t('patients.hospital_referral.sender_sor_failed', 'Klinikken blev ikke fundet i SOR.'));
    } else if (!senderSor.data.identifier || !senderSor.data.identifierCode) {
      problems.push(
        t(
          'patients.hospital_referral.sender_identifier_missing',
          'Lokationsnummeret kan ikke udledes af klinikkens SOR-enhed.',
        ),
      );
    } else if (senderSor.data.identifier.length > PARTY_MAX.identifier) {
      problems.push(tooLong(t('patients.hospital_referral.location_number', 'Lokationsnummer'), senderSor.data.identifier.length, PARTY_MAX.identifier));
    }
    const organisationName = clinic.name ?? '';
    if (organisationName.length === 0) {
      problems.push(t('patients.hospital_referral.sender_name_missing', 'Afsender mangler organisationsnavn.'));
    } else if (organisationName.length > PARTY_MAX.organisationName) {
      problems.push(tooLong(t('patients.hospital_referral.organisation', 'Organisation'), organisationName.length, PARTY_MAX.organisationName));
    }
    pushAddressLengths(problems, tooLong, clinicAddress);
    const phone = clinic.phone ?? '';
    if (phone.length > PARTY_MAX.telephone) {
      problems.push(tooLong(t('patients.hospital_referral.phone', 'Telefon'), phone.length, PARTY_MAX.telephone));
    }
    if (referrerQuery.isLoading) {
      pendingMessages.push(t('patients.hospital_referral.referrer_loading', 'Henvisers navn indlæses stadig.'));
    } else if (referrerQuery.isError) {
      problems.push(t('patients.hospital_referral.referrer_failed', 'Henvisers navn kunne ikke hentes.'));
    } else {
      const referrerName = referrerQuery.data?.name ?? '';
      if (referrerName.trim().length === 0) {
        problems.push(
          t('patients.hospital_referral.referrer_missing', 'Henvisers navn er ikke sat på brugeren.'),
        );
      } else if (referrerName.length > PARTY_MAX.referrerName) {
        problems.push(tooLong(t('patients.hospital_referral.referrer_name', 'Henvisers navn'), referrerName.length, PARTY_MAX.referrerName));
      }
      const referrerTitle = referrerQuery.data?.title ?? '';
      if (referrerTitle.trim().length === 0) {
        problems.push(
          t(
            'patients.hospital_referral.referrer_title_missing',
            'Henvisers titel er ikke sat. Sæt den under Indstillinger → Brugere → Titel.',
          ),
        );
      } else if (referrerTitle.length > PARTY_MAX.referrerTitle) {
        problems.push(
          tooLong(
            t('patients.hospital_referral.referrer_title', 'Henvisers titel'),
            referrerTitle.length,
            PARTY_MAX.referrerTitle,
          ),
        );
      }
    }
    for (const value of [ean, organisationName, clinicAddress.street, clinicAddress.postCode, clinicAddress.district, phone]) {
      if (!isLatin1(value)) {
        problems.push(latin1Message(t('patients.hospital_referral.sender_section', 'Afsender')));
        break;
      }
    }
    return problems;
  }

  function senderContactWarningMessages(): string[] {
    if (clinicQuery.isLoading || !clinicQuery.data) return [];
    const gaps = senderContactGaps(clinicAddress.postCode, clinicQuery.data.phone ?? '');
    if (gaps.length === 0) return [];
    return [senderContactWarningText(t, gaps)];
  }

  const assessment = assess();
  const alertMessages = [
    ...assessment.blocking,
    ...(attempted ? assessment.required : []),
    ...(attempted ? assessment.pending : []),
  ];

  function fileUploadContext(): {
    senderEan: string;
    senderName: string;
    receiverEan: string;
    receiverName: string;
    cpr: string;
    givenName: string;
    surname: string;
  } | null {
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    const givenName = patient.first_name?.trim() ?? '';
    const surname = patient.last_name?.trim() ?? '';
    const receiverName = receiver?.organisation.name?.trim() ?? '';
    if (!clinic?.name || !connection?.ean || !receiver?.organisation.ean || !receiverName) return null;
    if (!patientCprDigits || !givenName || !surname) return null;
    return {
      senderEan: connection.ean,
      senderName: clinic.name,
      receiverEan: receiver.organisation.ean,
      receiverName,
      cpr: patientCprDigits,
      givenName,
      surname,
    };
  }

  async function uploadFileRow(
    row: SpecialistAttachment,
    all: readonly SpecialistAttachment[],
  ): Promise<SpecialistAttachment> {
    if (!row.file) {
      return {
        ...row,
        error: t('patients.hospital_referral.attachment_file_missing', 'Vælg filen igen.'),
      };
    }
    const context = fileUploadContext();
    if (!context) {
      return {
        ...row,
        error: t(
          'patients.hospital_referral.attachment_file_context',
          'Vælg modtager, og udfyld patientens fornavn og efternavn, før filen kan vedlægges.',
        ),
      };
    }
    const others = all.filter((item) => item.key !== row.key && item.fileName.trim().length > 0);
    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await row.file.arrayBuffer());
    } catch {
      return {
        ...row,
        error: t('patients.hospital_referral.attachment_file_unreadable', 'Filen kunne ikke læses.'),
      };
    }
    try {
      const result = await callFunction<
        {
          clinicId: string;
          kind: 'XBIN02';
          sender: { ean: string; name: string };
          receiver: { ean: string; name: string };
          patientCpr: string;
          patientGivenName: string;
          patientSurname: string;
          fileName: string;
          fileBase64: string;
          existingFileCount: number;
          existingTotalBytes: number;
          objectType?: string;
        },
        MedbinFileResult
      >('communicate-medbin', {
        clinicId: patient.clinic_id,
        kind: 'XBIN02',
        sender: { ean: context.senderEan, name: context.senderName },
        receiver: { ean: context.receiverEan, name: context.receiverName },
        patientCpr: context.cpr,
        patientGivenName: context.givenName,
        patientSurname: context.surname,
        fileName: row.file.name,
        fileBase64: bytesToBase64(bytes),
        existingFileCount: others.length,
        existingTotalBytes: others.reduce((sum, item) => sum + item.byteLength, 0),
        ...(medbinObjectType(row.file.name, row.zipObjectType)
          ? { objectType: medbinObjectType(row.file.name, row.zipObjectType) }
          : {}),
      });
      const failed = result.checks.find((check) => !check.pass);
      if (!result.ok || failed) {
        return { ...row, error: failed?.detail || result.checks[0]?.detail || gatewayFallback(t) };
      }
      const parentReference = result.parentReference?.trim() ?? '';
      if (
        !result.exchangeId ||
        parentReference.length === 0 ||
        parentReference.includes('-') ||
        parentReference.length > 35 ||
        !result.objectCode ||
        !result.objectExtensionCode ||
        !Number.isInteger(result.originalObjectSize) ||
        result.originalObjectSize < 0
      ) {
        return {
          ...row,
          error: t('patients.hospital_referral.attachment_file_not_ready', 'Filen er ikke lagt på bilaget.'),
        };
      }
      return {
        ...row,
        error: '',
        fileName: row.file.name,
        byteLength: bytes.byteLength,
        exchangeId: result.exchangeId,
        objectIdentifier: parentReference,
        objectCode: result.objectCode,
        objectExtensionCode: result.objectExtensionCode,
        originalObjectSize: result.originalObjectSize,
      };
    } catch (err) {
      return { ...row, error: gatewayFailureText(err, gatewayFallback(t)) };
    }
  }

  async function uploadOutstandingFiles(
    rows: readonly SpecialistAttachment[],
  ): Promise<SpecialistAttachment[]> {
    const next: SpecialistAttachment[] = [];
    for (const row of rows) {
      if (row.exchangeId || !row.file) {
        next.push(row);
        continue;
      }
      next.push(await uploadFileRow(row, rows));
    }
    return next;
  }

  function failSubmit(message: string) {
    setSubmitError(message);
    toast.error(message, { duration: 10_000 });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const next = assess();
    setAttempted(true);
    const messages = [...next.blocking, ...next.required, ...next.pending];
    if (messages.length > 0) {
      focusField(next.focus);
      return;
    }
    setPending(true);
    setSubmitError(null);
    const prepared = await uploadOutstandingFiles(attachments);
    setAttachments(prepared);
    const refused = prepared.find((row) => row.fileName.trim().length > 0 && !row.exchangeId);
    if (refused) {
      setAttachmentError(refused.error || gatewayFallback(t));
      failSubmit(refused.error || gatewayFallback(t));
      setPending(false);
      return;
    }
    const body = buildRequest(prepared);
    if (!body) {
      failSubmit(t('patients.hospital_referral.toast_failed', 'Henvisningen kunne ikke dannes.'));
      setPending(false);
      return;
    }
    try {
      const result = await callFunction<Xref06Request, RefPreview>('communicate-ref', body);
      setPreview(result);
      if (result.ok) {
        toast.success(t('patients.hospital_referral.toast_built', 'Henvisningen er dannet.'));
        onCreated?.({ outboxId: result.outboxId, exchangeId: result.id });
      } else {
        toast.error(
          t(
            'patients.hospital_referral.toast_checks_failed',
            'Henvisningen er dannet, men kontrollen fandt mangler.',
          ),
        );
      }
    } catch (err) {
      failSubmit(
        presentDetail(
          referralBuildFailureMessage(
            err,
            t('patients.hospital_referral.toast_failed', 'Henvisningen kunne ikke dannes.'),
          ),
        ),
      );
    } finally {
      setPending(false);
    }
  }

  function focusField(focus: FocusTarget | null) {
    const target = focus === 'diagnosis' ? diagnosisRef.current : focus === 'anamnesis' ? anamnesisRef.current : null;
    target?.focus();
  }

  function buildRequest(rows: readonly SpecialistAttachment[]): Xref06Request | null {
    if (!receiver || !diagnoseCode) return null;
    const receiverPair = receiverIdentifierPair(identifierParty(receiver));
    if (!medicalSpecialityCode || !patientCprDigits || !receiverPair.ok) return null;
    if (anamnesis.trim().length === 0 || anamnesisOver || clinicalReasonOver) return null;
    const attachmentResult = referralReferencesForRequest(rows.map(attachmentDraft), {
      requireChecked: true,
    });
    if (!attachmentResult.ok) return null;
    const references = attachmentResult.references.filter(
      (reference): reference is BinReference => reference.kind === 'bin',
    );
    if (references.length !== attachmentResult.references.length) return null;
    const companions = referralCompanions(rows);
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    const sender = senderSor.data;
    const referrerName = referrerQuery.data?.name?.trim() ?? '';
    const referrerTitle = referrerQuery.data?.title?.trim() ?? '';
    if (
      !clinic ||
      !connection?.ean ||
      !sender?.identifier ||
      !sender.identifierCode ||
      !referrerName ||
      !referrerTitle
    ) {
      return null;
    }
    const chosenNames = chosenRelative
      ? relatedQuery.data?.find((row) => row.id === chosenRelative.row.to_patient_id)
      : undefined;
    const relationCode = chosenRelative ? medcomRelationCode(chosenRelative.relationType) : null;
    const relativeDigits = relativeCpr ? cprDigits(relativeCpr) : null;
    const telephoneSubscribers = subscribers.slice(0, MEDCOM_MAX_TELEPHONE_SUBSCRIBERS).map((subscriber) => ({
      telephoneSubscriberIdentifier: subscriber.identifier,
      telephoneSubscriberCode: subscriber.code,
    }));

    const additionalInformation = xref06AdditionalInformation(clinicalReason);
    const request: Xref06Request = {
      clinicId: patient.clinic_id,
      patientId: patient.id,
      kind: 'XREF06',
      acknowledgementCode: 'pluspositivkvitt',
      sender: {
        ean: connection.ean,
        identifier: sender.identifier,
        identifierCode: sender.identifierCode,
        organisationName: clinic.name,
        medicalSpecialityCode,
        referrer: { personName: referrerName, personTitle: referrerTitle },
        ...filledAddress(clinicAddress),
        ...(clinic.phone ? { telephoneSubscriberIdentifier: clinic.phone } : {}),
      },
      receiver: receiverBody(receiver, receiverPair.identifierCode),
      patient: {
        civilRegistrationNumber: patientCprDigits,
        personSurnameName: patient.last_name,
        ...(patient.first_name ? { personGivenName: patient.first_name } : {}),
        ...(patient.job_title ? { personTitle: patient.job_title } : {}),
        ...filledAddress(address),
        ...(patient.email ? { emailIdentifier: patient.email } : {}),
        ...(telephoneSubscribers.length > 0 ? { telephoneSubscribers } : {}),
      },
      episodeOfCareIdentifier: episodeId,
      relevantClinicalInformation: {
        anamnesis: [anamnesis, eboksSentence],
      },
      ...(additionalInformation ? { additionalInformation } : {}),
      referral: {
        refer: {
          diagnoseCode,
          diagnoseTypeCode: DIAGNOSE_TYPE_CODE,
          diagnoseText: diagnoseLabel(diagnoseCode),
        },
      },
      ...(references.length > 0 ? { references } : {}),
      ...(companions.length > 0 ? { companions } : {}),
    };
    if (chosenNames && relationCode && chosenNames.last_name) {
      request.relative = {
        relationCode,
        personSurnameName: chosenNames.last_name,
        ...(chosenNames.first_name ? { personGivenName: chosenNames.first_name } : {}),
        ...(relativeDigits ? { personIdentifier: relativeDigits } : {}),
      };
    }
    return request;
  }

  const senderView = senderDisplay();
  const selectedBlockedReason = receiver ? receiverBlockedReason(receiver.organisation) : null;

  const patientName = `${patient.first_name} ${patient.last_name}`.trim();

  return (
    <form onSubmit={onSubmit} className="flex flex-none flex-col">
      <ReferralFormChrome
        backTo={patientHenvisningPath(patient.id, { vis: 'sendte' })}
        backLabel={t('patients.hospital_referral.back_to_sent', 'Sendte henvisninger')}
        title={t('patients.hospital_referral.new_specialist', 'Ny speciallægehenvisning')}
        subtitle={t('patients.hospital_referral.form_subtitle', 'Elektronisk henvisning for {{name}}', {
          name: patientName,
        })}
        onCancel={onCancel}
        banner={
          <ReferralBlockingNotice
            messages={alertMessages}
            relationsTo={childWithoutRelative ? patientTabPath(patient.id, 'relations') : undefined}
            relativesHref={childWithoutRelative ? '#paaroerende' : undefined}
          />
        }
        toc={[
          { id: 'modtager', label: t('patients.hospital_referral.receiver_section', 'Modtager') },
          { id: 'diagnoser', label: t('patients.hospital_referral.toc_diagnoses', 'Diagnoser') },
          { id: 'undersogelse', label: t('patients.hospital_referral.toc_exam', 'Ønsket undersøgelse') },
          { id: 'kliniske', label: t('patients.hospital_referral.clinical_section', 'Kliniske oplysninger') },
          { id: 'eboks', label: t('patients.specialist_referral.eboks_section', 'Samtykke til indkaldelse via e-boks/mit.dk') },
          { id: 'bilag', label: t('patients.specialist_referral.attachments_section', 'Bilag') },
          { id: 'paaroerende', label: t('patients.hospital_referral.relatives_section', 'Pårørende') },
          { id: 'patient', label: t('patients.hospital_referral.patient_section', 'Patient fra stamkort') },
          { id: 'afsender', label: t('patients.hospital_referral.sender_section', 'Afsender') },
          { id: 'kvittering', label: t('patients.hospital_referral.acknowledgement_section', 'Kvittering') },
        ]}
        footerStatus={
          receiver ? (
            <>
              <Hospital className="h-4 w-4" aria-hidden />
              <span className="min-w-0 truncate">
                {t('patients.hospital_referral.footer_receiver', 'Modtager: {{name}} · EAN {{ean}}', {
                  name: receiver.organisation.name || receiver.identifier,
                  ean: receiver.organisation.ean || receiver.identifier,
                })}
              </span>
            </>
          ) : undefined
        }
        footer={
          <Button type="submit" disabled={pending} loading={pending} loadingText={t('patients.hospital_referral.submitting', 'Danner henvisning…')}>
            <Send aria-hidden />
            {t('patients.hospital_referral.submit', 'Dan henvisning')}
          </Button>
        }
      >
      <Section id="modtager" title={t('patients.hospital_referral.receiver_section', 'Modtager')}>
        <SorPicker
          label={t('patients.hospital_referral.receiver_search', 'Modtager')}
          fieldId="specialist-referral-receiver"
          selected={receiver}
          partyLabel={t('patients.hospital_referral.receiver_section', 'Modtager')}
          receives={SPECIALIST_RECEIVES}
          hide={(organisation) => ref06OnlyBlocked(organisation, diagnoseCode)}
          disabledReason={(organisation) =>
            dnhfBlocked(organisation, diagnoseCode) ? dnhfMessage() : null
          }
          deliveryNote={(organisation) =>
            receivesEdifactOnly(organisation)
              ? t(
                  'patients.specialist_referral.delivery_edifact',
                  'Modtageren læser kun EDIFACT (REF06). Henvisningen konverteres til EDIFACT.',
                )
              : null
          }
          onSelect={(organisation) =>
            setReceiver({
              organisation,
              identifier: organisation.identifier ?? '',
              chosenIdentifierCode: null,
            })
          }
          onIdentifier={(identifier) =>
            setReceiver((current) => (current ? { ...current, identifier } : current))
          }
          onIdentifierCode={(chosenIdentifierCode) =>
            setReceiver((current) => (current ? { ...current, chosenIdentifierCode } : current))
          }
          onClear={() => setReceiver(null)}
        />
        {attempted && !receiver && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.receiver_missing', 'Vælg en modtager.')}
          </p>
        )}
        {selectedBlockedReason && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {selectedBlockedReason}
          </p>
        )}
      </Section>

      <Section id="diagnoser" title={t('patients.hospital_referral.diagnosis_section', 'Henvisningsdiagnose')}>
        <div>
          <label htmlFor="specialist-referral-diagnosis" className={cn('block', FORM_LABEL_TYPE)}>
            {t('patients.hospital_referral.diagnosis_section', 'Henvisningsdiagnose')}
          </label>
          <select
            id="specialist-referral-diagnosis"
            ref={diagnosisRef}
            value={diagnoseCode ?? ''}
            onChange={(event) => {
              const next = event.target.value;
              setDiagnoseCode(isSpecialistDiagnoseCode(next) ? next : null);
            }}
            aria-invalid={attempted && diagnoseCode === null}
            className={cn(
              TYPE.inputValue,
              'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ',
              attempted && diagnoseCode === null && 'border-ds-red focus:border-ds-red focus:ring-ds-red',
            )}
          >
            <option value="">{t('patients.specialist_referral.diagnose_choose', 'Vælg henvisningsdiagnose')}</option>
            {DIAGNOSE_CODES.map((code) => (
              <option key={code} value={code}>
                {diagnoseLabel(code)}
              </option>
            ))}
          </select>
        </div>
        {attempted && !diagnoseCode && (
          <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
            {t('patients.hospital_referral.diagnosis_required', WARNING_DIAGNOSIS)}
          </p>
        )}
        {diagnoseCode === '6' && (
          <div className="mt-3 space-y-2">
            <p className={UI_MUTED}>
              {t(
                'patients.specialist_referral.faps_notice',
                'Ved henvisning til ”vurdering ved enkeltstående konsultation” skal det præciseres, hvad den alment praktiserende læge ønsker at få vurderet.',
              )}
            </p>
            <p className={UI_MUTED}>
              <a
                href={FAPS_ENKELTSTAAENDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {t('patients.specialist_referral.faps_link', 'FAPS-oversigt over målgrupper')}
              </a>
            </p>
          </div>
        )}
      </Section>

      <Section id="undersogelse" title={clinicalReasonLabel}>
        <TextArea
          id="specialist-referral-clinical-reason"
          fieldLabel={clinicalReasonLabel}
          textRef={clinicalReasonRef}
          value={clinicalReason}
          onChange={setClinicalReason}
          max={XREF06_CLINICAL_REASON_MAX}
        />
      </Section>

      <Section id="kliniske" title={anamnesisLabel}>
        <p className={UI_MUTED}>{ANAMNESIS_PROMPT}</p>
        <div className="mt-3">
          <TextArea
            id="specialist-referral-anamnesis"
            fieldLabel={anamnesisLabel}
            textRef={anamnesisRef}
            value={anamnesis}
            onChange={setAnamnesis}
            max={CLINICAL_INFO_MAX_TOTAL_XREF06 - eboksSentence.length}
          />
          {anamnesisOver && (
            <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
              {anamnesisTooLong()}
            </p>
          )}
          {attempted && anamnesis.trim().length === 0 && (
            <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
              {t('patients.hospital_referral.anamnesis_required', WARNING_ANAMNESIS)}
            </p>
          )}
        </div>
      </Section>

      <Section id="eboks" title={t('patients.specialist_referral.eboks_section', 'Samtykke til indkaldelse via e-boks/mit.dk')}>
        <RadioGroup
          name="specialist-referral-eboks"
          legend={t(
            'patients.specialist_referral.eboks_legend',
            'Har patienten givet samtykke til indkaldelse via e-boks/mit.dk?',
          )}
          value={eboksConsent}
          onChange={setEboksConsent}
          options={[
            { value: 'ja', label: t('patients.specialist_referral.eboks_ja', 'Ja') },
            { value: 'nej', label: t('patients.specialist_referral.eboks_nej', 'Nej') },
            {
              value: 'ikke_oplyst',
              label: t('patients.specialist_referral.eboks_unknown', 'Ikke oplyst'),
            },
          ]}
        />
        <p className={cn('mt-2', UI_MUTED)}>
          {t(
            'patients.specialist_referral.eboks_hint',
            'Sætningen tilføjes til sidst i teksten, der sendes, og tæller med i grænsen på 6300 tegn: {{sentence}}',
            { sentence: eboksSentence },
          )}
        </p>
      </Section>

      <Section id="bilag" title={t('patients.specialist_referral.attachments_section', 'Bilag')}>
        <p className={UI_MUTED}>
          {t('patients.specialist_referral.attachments_hint', 'Vedlæg en eller flere filer til henvisningen.')}
        </p>
        <div className="mt-3">
          <Input
            id="specialist-referral-attachments"
            type="file"
            multiple
            accept={XML_ATTACHMENT_ACCEPT}
            label={t('patients.specialist_referral.attachments_file', 'Filer')}
            disabled={pending}
            error={attachmentError ?? undefined}
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              event.target.value = '';
              onPickAttachments(picked);
            }}
          />
        </div>
        {attachments.length > 0 && (
          <ul className="mt-3 space-y-2">
            {attachments.map((item) => (
              <li key={item.key} className="space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <span className={TYPE.body}>
                    {item.fileName} ({Math.ceil(item.byteLength / 1024)} KB)
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAttachments((rows) => rows.filter((row) => row.key !== item.key));
                      setAttachmentError(null);
                    }}
                  >
                    {t('patients.specialist_referral.attachments_remove', 'Fjern')}
                  </Button>
                </div>
                {needsZipObjectType(item.fileName) && (
                  <ZipContentSelect
                    id={`specialist-referral-zip-${item.key}`}
                    value={item.zipObjectType}
                    disabled={pending}
                    onChange={(zipObjectType) => {
                      // A new content type means a new XBIN02: the file is uploaded again on submit.
                      setAttachments((rows) =>
                        rows.map((row) =>
                          row.key === item.key
                            ? {
                                ...row,
                                zipObjectType,
                                exchangeId: null,
                                objectIdentifier: '',
                                objectCode: '',
                                objectExtensionCode: '',
                                originalObjectSize: null,
                                error: '',
                              }
                            : row,
                        ),
                      );
                    }}
                  />
                )}
                {item.error && (
                  <p role="alert" className={FORM_ERROR_TYPE}>
                    {item.error}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        id="paaroerende"
        title={t('patients.hospital_referral.relatives_section', 'Pårørende')}
        hint={
          relativeUnder15
            ? t(
                'patients.hospital_referral.relatives_choose_under15',
                WARNING_UNDER15_CHOOSE_RELATIVE,
              )
            : t(
                'patients.hospital_referral.relatives_choose',
                'Vælg den pårørende, der medtages i henvisningen. Der kan højst medtages én. Som udgangspunkt vælges moren, ellers faren, ellers den første øvrige personrelation.',
              )
        }
      >
        <ReferralRelativesChoice
          idPrefix="specialist-referral"
          relations={personRelations}
          selectedId={chosenRelative?.row.id ?? null}
          under15={relativeUnder15}
          childWithoutRelative={childWithoutRelative}
          emptyLabel={emptyLabel}
          onChoose={setRelativeChoice}
        />
      </Section>

      <Section
        id="patient"
        title={t('patients.hospital_referral.patient_section', 'Patient fra stamkort')}
        hint={t('patients.hospital_referral.stamkort_hint', 'Oplysningerne hentes fra stamkortet og rettes under Rediger patient.')}
      >
        <Grid>
          <ReadOnly
            label={t('patients.hospital_referral.cpr', 'CPR')}
            value={cprDisplay(patient.has_cpr, canRevealCpr, cprFailed, revealedCpr, emptyLabel, t)}
          />
          <ReadOnly label={t('patients.hospital_referral.surname', 'Efternavn')} value={patient.last_name || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.given_name', 'Fornavn')} value={patient.first_name || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.street', 'Adresse')} value={address.street || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.post_code', 'Postnr')} value={address.postCode || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.city', 'By')} value={address.district || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.job_title', 'Stilling')} value={patient.job_title || emptyLabel} />
          <ReadOnly label={t('patients.hospital_referral.email', 'E-mail')} value={patient.email || emptyLabel} />
          {PHONE_ROWS.map((row) => (
            <ReadOnly
              key={row.code}
              label={t(`patients.hospital_referral.phone_${row.code}`, row.label)}
              value={
                subscribers
                  .filter((subscriber) => subscriber.code === row.code)
                  .map((subscriber) => subscriber.identifier)
                  .join(', ') || emptyLabel
              }
            />
          ))}
        </Grid>
      </Section>

      <Section id="afsender" title={t('patients.hospital_referral.sender_section', 'Afsender')}>
        <Grid>
          <ReadOnly label={t('patients.hospital_referral.ean', 'EAN')} value={senderView.ean} />
          <ReadOnly
            label={t('patients.hospital_referral.location_number', 'Lokationsnummer')}
            value={senderView.location}
          />
          <ReadOnly
            label={t('patients.hospital_referral.identifier_code', 'Identifikatorkode')}
            value={senderView.identifierCode}
          />
          <ReadOnly
            label={t('patients.hospital_referral.organisation', 'Organisation')}
            value={senderView.organisation}
          />
          <ReadOnly label={t('patients.hospital_referral.street', 'Adresse')} value={senderView.street} />
          <ReadOnly label={t('patients.hospital_referral.post_code', 'Postnr')} value={senderView.postCode} />
          <ReadOnly label={t('patients.hospital_referral.city', 'By')} value={senderView.city} />
          <ReadOnly label={t('patients.hospital_referral.phone', 'Telefon')} value={senderView.phone} />
          {assessment.warnings.length > 0 ? (
            <div
              role="status"
              className="rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 sm:col-span-2"
            >
              {assessment.warnings.map((message, index) => (
                <p
                  key={`${index}-${message}`}
                  className={cn(TYPE.body, 'text-ds-amber-ink ')}
                >
                  {message}
                </p>
              ))}
            </div>
          ) : null}
          <ReadOnly
            label={t('patients.hospital_referral.speciality', 'Speciale')}
            value={
              medicalSpecialityCode
                ? medicalSpecialityLabel(medicalSpecialityCode)
                : t('patients.hospital_referral.speciality_missing', 'Ikke konfigureret')
            }
          />
          <ReadOnly
            label={t('patients.hospital_referral.referrer_name', 'Henvisers navn')}
            value={senderView.referrer}
          />
          <ReadOnly
            label={t('patients.hospital_referral.referrer_title', 'Henvisers titel')}
            value={senderView.referrerTitle}
          />
        </Grid>
        {medicalSpecialityCode === null && (
          <p role="alert" className={cn('mt-3', FORM_ERROR_TYPE)}>
            {t(
              'patients.hospital_referral.speciality_block',
              'Klinikkens specialekode er ikke sat op. Henvisningen kan ikke dannes, før specialekoden er konfigureret.',
            )}
          </p>
        )}
      </Section>

      <Section id="kvittering" title={t('patients.hospital_referral.acknowledgement_section', 'Kvittering')}>
        <p className={TYPE.body}>
          {t('patients.hospital_referral.acknowledgement', 'Der bedes om positiv kvittering.')}
        </p>
      </Section>

      <ReferralSubmitError message={submitError} />
      </ReferralFormChrome>

      {preview ? (
        <ReferralBuiltPreview
          xml={preview.xml}
          checks={preview.checks}
          ok={preview.ok}
          edi={preview.edi}
          xmlFileName={`XREF06_${episodeId}.xml`}
          ediFileName={`REF06_${episodeId}.edi`}
          presentDetail={presentDetail}
        />
      ) : null}
    </form>
  );

  function dnhfMessage(): string {
    return t(
      'patients.specialist_referral.dnhf_blocked',
      'Henvisningshotellet (DNHF) kan ikke vælges som modtager ved henvisningsårsag 6. Vælg en konkret speciallæge.',
    );
  }

  function ref06OnlyMessage(): string {
    return t(
      'patients.specialist_referral.ref06_only_blocked',
      'Modtagere der kun læser REF06 kan ikke vælges ved henvisningsårsag 6. Vælg en modtager der læser XREF06.',
    );
  }

  function receiverBlockedReason(
    organisation: { ediReceiveTypes: readonly string[]; parentSorId: string | null },
  ): string | null {
    if (dnhfBlocked(organisation, diagnoseCode)) return dnhfMessage();
    if (ref06OnlyBlocked(organisation, diagnoseCode)) return ref06OnlyMessage();
    return null;
  }

  function senderDisplay() {
    const loading = t('patients.hospital_referral.loading', 'Henter…');
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    if (clinicQuery.isLoading || connectionQuery.isLoading) {
      return blankSender(loading);
    }
    const location = !connection?.sor_id
      ? t('patients.hospital_referral.sender_sor_missing', 'Klinikken har ikke et SOR-id.')
      : senderSor.isLoading
        ? loading
        : senderSor.data?.identifier ??
          t(
            'patients.hospital_referral.sender_identifier_missing',
            'Lokationsnummeret kan ikke udledes af klinikkens SOR-enhed.',
          );
    return {
      ean: connection?.ean || emptyLabel,
      location,
      identifierCode: senderSor.data?.identifierCode || emptyLabel,
      organisation: clinic?.name || emptyLabel,
      street: clinicAddress.street || emptyLabel,
      postCode: clinicAddress.postCode || emptyLabel,
      city: clinicAddress.district || emptyLabel,
      phone: clinic?.phone || emptyLabel,
      referrer: referrerQuery.isLoading
        ? loading
        : referrerQuery.data?.name || emptyLabel,
      referrerTitle: referrerQuery.isLoading
        ? loading
        : referrerQuery.data?.title || emptyLabel,
    };
  }
}

function isChildAge(cpr: string, today: Date): boolean {
  return under15BlocksReferral(cpr, [], today);
}

function blankSender(value: string) {
  return {
    ean: value,
    location: value,
    identifierCode: value,
    organisation: value,
    street: value,
    postCode: value,
    city: value,
    phone: value,
    referrer: value,
    referrerTitle: value,
  };
}

function cprDisplay(
  hasCpr: boolean,
  canReveal: boolean,
  failed: boolean,
  revealed: string | null,
  emptyLabel: string,
  t: (key: string, fallback: string) => string,
): string {
  if (!hasCpr) return emptyLabel;
  if (!canReveal) return t('patients.hospital_referral.cpr_unavailable', 'CPR-nummeret kan ikke vises. Henvisningen kan ikke dannes.');
  if (failed) return t('patients.hospital_referral.cpr_failed', 'CPR-nummeret kunne ikke læses. Henvisningen kan ikke dannes.');
  if (!revealed) return t('patients.hospital_referral.loading', 'Henter…');
  return formatCprDisplay(revealed);
}

function senderContactWarningText(
  t: (key: string, fallback: string) => string,
  gaps: readonly SenderContactGap[],
): string {
  const missingPostcode = gaps.includes('postcode');
  const missingPhone = gaps.includes('phone');
  if (missingPostcode && missingPhone) {
    return t(
      'patients.hospital_referral.sender_contact_missing_both',
      'Klinikkens postnummer og telefonnummer mangler. MedComs egne eksempler har altid begge på afsenderen. Udfyld dem under klinikkens indstillinger.',
    );
  }
  if (missingPostcode) {
    return t(
      'patients.hospital_referral.sender_postcode_missing',
      'Klinikkens postnummer mangler. MedComs egne eksempler har altid et postnummer på afsenderen. Udfyld det under klinikkens indstillinger.',
    );
  }
  return t(
    'patients.hospital_referral.sender_phone_missing',
    'Klinikkens telefonnummer mangler. MedComs egne eksempler har altid et telefonnummer på afsenderen. Udfyld det under klinikkens indstillinger.',
  );
}

function filledAddress(address: { street: string; postCode: string; district: string }): {
  streetName?: string;
  postCodeIdentifier?: string;
  districtName?: string;
} {
  return {
    ...(address.street ? { streetName: address.street } : {}),
    ...(address.postCode ? { postCodeIdentifier: address.postCode } : {}),
    ...(address.district ? { districtName: address.district } : {}),
  };
}

function identifierParty(picked: PickedReceiver): ReceiverIdentifierParty {
  return {
    registerCode: picked.organisation.identifierCode,
    chosenCode: picked.chosenIdentifierCode,
    identifier: picked.identifier,
  };
}

function receiverBody(picked: PickedReceiver, identifierCode: Xref01IdentifierCode): Xref06Request['receiver'] {
  return {
    ean: picked.organisation.ean,
    identifier: picked.identifier,
    identifierCode,
    ...(picked.organisation.name ? { organisationName: picked.organisation.name } : {}),
    ...filledAddress({
      street: picked.organisation.address.street ?? '',
      postCode: picked.organisation.address.postCode ?? '',
      district: picked.organisation.address.district ?? '',
    }),
  };
}

function pushAddressLengths(
  problems: string[],
  tooLong: (field: string, length: number, max: number) => string,
  address: { street: string; postCode: string; district: string },
) {
  if (address.street.length > PARTY_MAX.street) {
    problems.push(tooLong('Adresse', address.street.length, PARTY_MAX.street));
  }
  if (address.postCode.length > PARTY_MAX.postCode) {
    problems.push(tooLong('Postnr', address.postCode.length, PARTY_MAX.postCode));
  }
  if (address.district.length > PARTY_MAX.district) {
    problems.push(tooLong('By', address.district.length, PARTY_MAX.district));
  }
}

function identifierGapMessages(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  label: string,
  missing: IdentifierPairGap,
): string[] {
  const codeMessage = t(
    'patients.hospital_referral.identifier_code_missing',
    '{{label}}: Identifikatorkoden mangler.',
    { label },
  );
  const identifierMessage = t(
    'patients.hospital_referral.identifier_missing',
    '{{label}}: Identifikatoren mangler.',
    { label },
  );
  switch (missing) {
    case 'both':
      return [codeMessage, identifierMessage];
    case 'code':
      return [codeMessage];
    case 'identifier':
      return [identifierMessage];
    default: {
      const unexpected: never = missing;
      return [unexpected];
    }
  }
}

function partyProblems(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  tooLong: (field: string, length: number, max: number) => string,
  label: string,
  picked: PickedReceiver,
): string[] {
  const problems: string[] = [];
  const org = picked.organisation;
  const pair = receiverIdentifierPair(identifierParty(picked));
  if (!pair.ok) problems.push(...identifierGapMessages(t, label, pair.missing));
  if (picked.identifier.length > PARTY_MAX.identifier) {
    problems.push(tooLong(`${label} identifikator`, picked.identifier.length, PARTY_MAX.identifier));
  }
  if (org.ean.length > PARTY_MAX.ean) problems.push(tooLong(`${label} EAN`, org.ean.length, PARTY_MAX.ean));
  if (org.name.length > PARTY_MAX.organisationName) {
    problems.push(tooLong(`${label} organisation`, org.name.length, PARTY_MAX.organisationName));
  }
  const street = org.address.street ?? '';
  const postCode = org.address.postCode ?? '';
  const district = org.address.district ?? '';
  if (street.length > PARTY_MAX.street) problems.push(tooLong(`${label} adresse`, street.length, PARTY_MAX.street));
  if (postCode.length > PARTY_MAX.postCode) problems.push(tooLong(`${label} postnr`, postCode.length, PARTY_MAX.postCode));
  if (district.length > PARTY_MAX.district) problems.push(tooLong(`${label} by`, district.length, PARTY_MAX.district));
  for (const value of [org.ean, org.name, street, postCode, district, picked.identifier]) {
    if (firstNonLatin1(value) !== null) {
      problems.push(
        t(
          'patients.hospital_referral.latin1',
          '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
          { field: label },
        ),
      );
      break;
    }
  }
  return problems;
}

type SpecialistAttachment = {
  key: string;
  description: string;
  fileName: string;
  byteLength: number;
  file: File | null;
  objectIdentifier: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number | null;
  exchangeId: string | null;
  error: string;
  /** C11: what a zip file holds (BIN01-S 3.3). */
  zipObjectType?: ZipObjectType;
};

type MedbinFileResult = {
  ok: boolean;
  parentReference: string | null;
  exchangeId: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number;
  checks: { id: string; pass: boolean; detail: string }[];
};

function attachmentDraft(row: SpecialistAttachment): FileAttachmentDraft {
  return {
    kind: 'file',
    description: row.description,
    fileName: row.fileName,
    byteLength: row.byteLength,
    objectIdentifier: row.objectIdentifier,
    objectCode: row.objectCode,
    objectExtensionCode: row.objectExtensionCode,
    originalObjectSize: row.originalObjectSize,
    zipObjectType: row.zipObjectType,
  };
}

function referralCompanions(rows: readonly SpecialistAttachment[]): { exchangeId: string; fileName: string }[] {
  const companions: { exchangeId: string; fileName: string }[] = [];
  for (const row of rows) {
    if (row.exchangeId && row.fileName.trim().length > 0) {
      companions.push({ exchangeId: row.exchangeId, fileName: row.fileName.trim() });
    }
  }
  return companions;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

function gatewayFallback(t: (key: string, fallback: string) => string): string {
  return t('patients.hospital_referral.attachment_file_refused', 'Filen blev afvist.');
}

function gatewayFailureText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiCallError)) return fallback;
  const message = err.message.trim();
  if (!message) return fallback;
  const code = detailCode(err.details);
  if (code && !message.includes(code)) return `${message} (${code})`;
  return message;
}

function detailCode(details: unknown): string | null {
  if (!details || typeof details !== 'object' || !('code' in details)) return null;
  const code = details.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}
